import { ErrorHandler } from '@angular/core';

/**
 * Recovers from "Failed to fetch dynamically imported module" — the error that
 * fires whenever the dashboard is deployed while a merchant has it open.
 *
 * Angular splits the build into hash-named chunks (chunk-LXDEEH7E.js); every
 * deploy produces new hashes. A tab opened before the deploy still holds the
 * old index.html, so navigating to a not-yet-loaded route (Analytics, etc.)
 * requests a chunk name the new deploy no longer serves. To the merchant the
 * dashboard just breaks; a manual refresh cures it — but they don't know that.
 * It surfaced as FRONTEND-DASHBOARD-ANGULAR-52 in Sentry, firing on deploy days.
 *
 * The fix: on exactly this failure, reload ONCE. The reload fetches the current
 * index.html with the current chunk names and the merchant lands on the page
 * they clicked. A sessionStorage guard prevents a reload loop — if a fresh
 * index.html STILL can't find the chunk, something is genuinely broken (a bad
 * deploy, a purged object) and must reach Sentry, not spin the tab forever.
 *
 * Everything that is not a chunk-load error is delegated untouched to the
 * wrapped handler, so Sentry reporting is unchanged for real bugs.
 */
export class ChunkLoadErrorHandler implements ErrorHandler {
  /** Survives the reload it triggers; cleared once the app boots cleanly. */
  static readonly reloadGuardKey = 'chunkReloadAttempted';

  /**
   * @param reload  Injected so the recovery is testable without touching the
   *                (read-only in jsdom) window.location.reload. Defaults to the
   *                real thing in production.
   */
  constructor(
    private readonly delegate: ErrorHandler,
    private readonly reload: () => void = () => window.location.reload(),
  ) {}

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && !this.alreadyReloaded()) {
      this.markReloaded();
      this.reload();
      return;
    }

    this.delegate.handleError(error);
  }

  /**
   * Call once from a clean app start to arm the next recovery. Without this
   * the guard, set on the recovering reload, would block a SECOND genuine
   * recovery later in the same tab session.
   */
  static clearReloadGuard(): void {
    try {
      sessionStorage.removeItem(ChunkLoadErrorHandler.reloadGuardKey);
    } catch {
      // sessionStorage unavailable (private mode edge cases) — nothing to clear.
    }
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = this.messageOf(error);
    // The three shapes browsers use for a missing dynamic import, plus the
    // name Angular/webpack give the older lazy-chunk failure.
    return (
      /Failed to fetch dynamically imported module/i.test(message) ||
      /error loading dynamically imported module/i.test(message) ||
      /Importing a module script failed/i.test(message) ||
      /ChunkLoadError/i.test(message) ||
      (typeof error === 'object' &&
        error !== null &&
        (error as { name?: string }).name === 'ChunkLoadError')
    );
  }

  private messageOf(error: unknown): string {
    if (error == null) return '';
    if (typeof error === 'string') return error;
    // Angular wraps the real error under `.rejection` for unhandled promise
    // rejections, and Error carries it on `.message`.
    const anyErr = error as { message?: unknown; rejection?: unknown };
    if (anyErr.rejection) return this.messageOf(anyErr.rejection);
    if (typeof anyErr.message === 'string') return anyErr.message;
    return String(error);
  }

  private alreadyReloaded(): boolean {
    try {
      return (
        sessionStorage.getItem(ChunkLoadErrorHandler.reloadGuardKey) === 'true'
      );
    } catch {
      // No sessionStorage → cannot guard a loop → don't auto-reload, report it.
      return true;
    }
  }

  private markReloaded(): void {
    try {
      sessionStorage.setItem(ChunkLoadErrorHandler.reloadGuardKey, 'true');
    } catch {
      // Best-effort; alreadyReloaded() already fails safe when storage is out.
    }
  }
}
